import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 lg:p-8" }
const _hoisted_2 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_3 = {
  key: 0,
  class: "flex bg-gray-50 p-4 shadow ring-1 ring-gray-300 md:rounded"
}
const _hoisted_4 = { class: "text-center text-xs font-medium text-gray-500" }
const _hoisted_5 = { class: "mt-1 text-center text-2xl font-semibold text-gray-800" }
const _hoisted_6 = { class: "grid grid-cols-1 gap-4 sm:grid-cols-2" }
const _hoisted_7 = { class: "rounded bg-gray-50 p-4 shadow" }
const _hoisted_8 = { class: "mb-4 text-lg font-semibold text-gray-800" }
const _hoisted_9 = { class: "space-y-4" }
const _hoisted_10 = { class: "flex justify-between border-b border-gray-200" }
const _hoisted_11 = { class: "text-sm font-medium text-gray-500" }
const _hoisted_12 = { class: "text-lg font-semibold text-gray-800" }
const _hoisted_13 = { class: "text-sm text-gray-500" }
const _hoisted_14 = { class: "text-sm font-semibold text-gray-800" }
const _hoisted_15 = { class: "rounded bg-gray-50 p-4 shadow" }
const _hoisted_16 = { class: "mb-4 text-lg font-semibold text-gray-800" }
const _hoisted_17 = { class: "space-y-4" }
const _hoisted_18 = { class: "flex justify-between border-b border-gray-200" }
const _hoisted_19 = { class: "text-sm font-medium text-gray-500" }
const _hoisted_20 = { class: "text-lg font-semibold text-gray-800" }
const _hoisted_21 = { class: "text-sm text-gray-500" }
const _hoisted_22 = { class: "text-sm font-semibold text-gray-800" }

import { ref, Ref, computed, watch, onMounted } from 'vue';
import moment from 'moment';
import { useI18n } from 'vue-i18n';

import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import { toastServiceError } from '@/services/core/notification';
import { BillingReportService } from '@/services';

import {
    BarChartConfig,
    BillingModel,
    BillingReportActions,
    BillingReportGetters,
    DataItem,
    FilterItem,
} from '@/types';

import { BillingReportFilters } from './components';


export default /*@__PURE__*/_defineComponent({
  __name: 'BillingReportPage',
  setup(__props) {

const reportInfo: Ref<BillingModel | null> = ref(null);

const dateRange = computed<Array<Date>>({
    get: () => filterStore.dateRange,
    set: (value) => filterStore.setDateRange(value),
});

const filter = computed(() => store.getters[BillingReportGetters.FILTER]);
const activeFilters = computed(() => store.getters[BillingReportGetters.ACTIVE_FILTERS]);

const filterStore = useFilterStore();
const uiStore = useUiStore();
const { t } = useI18n();

watch(
    [dateRange, filter],
    () => {
        fetchReportData();
    },
    { deep: true },
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchReportData = async () => {
    const params = {
        start_date: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
        end_date: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
        user_id: filter.value.user?.id || null,
        source_number: filter.value.sourceNumber?.value || null,
        destination_number: filter.value.destinationNumber?.value || null,
        status: filter.value.status?.value || null,
        type: filter.value.type?.value || null,
    };

    try {
        setLoading(true);
        const response = await BillingReportService.getInfo({ params });
        reportInfo.value = response.data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const handleRemoveFilter = (filter: FilterItem) => {
    store.dispatch(BillingReportActions.CLEAR_FILTER, filter.field);
};

const headerStats = computed<DataItem<string>[]>(() => {
    const totalMinutes = reportInfo.value?.totalMinutes || 0;
    const totalCost = reportInfo.value?.totalCost?.toFixed(2) || '0,00';

    return [
        {
            name: 'totalMinutes',
            value: `${Math.floor(totalMinutes)}:${Math.floor((totalMinutes % 1) * 60)
                .toString()
                .padStart(2, '0')}`,
        },
        {
            name: 'totalCost',
            value: `R$ ${totalCost}`,
        },
    ];
});

const getChart = (
    title: string,
    dataKey: keyof BillingModel['dailyResults'][0],
    color = '#BFDBFE',
): BarChartConfig => {
    const category =
        reportInfo.value?.dailyResults?.map((item) => moment(item.date).format('D [de] MMM')) || [];

    const value = reportInfo.value?.dailyResults?.map((item) => item[dataKey] || 0) || [];

    return {
        title,
        category,
        series: [
            {
                name: title,
                value,
                color,
            },
        ],
    };
};

const dailyConsumptionChart = computed(() =>
    getChart(t('report.billing.dailyConsumptionValue'), 'cost', '#00C3AE'),
);

onMounted(() => {
    fetchReportData();
});

return (_ctx: any,_cache: any) => {
  const _component_UiDatePicker = _resolveComponent("UiDatePicker")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiBadge = _resolveComponent("UiBadge")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_BarChart = _resolveComponent("BarChart")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('report.billing.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createVNode(_unref(BillingReportFilters), {
          sources: reportInfo.value?.sources || [],
          destinations: reportInfo.value?.destinations || []
        }, null, 8, ["sources", "destinations"]),
        _createVNode(_component_UiDatePicker, {
          modelValue: dateRange.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateRange).value = $event)),
          range: "",
          multiCalendars: "",
          class: "ml-4"
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }),
    (activeFilters.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (filter) => {
            return (_openBlock(), _createBlock(_component_UiBadge, {
              key: filter,
              removable: "",
              onRemove: ($event: any) => (handleRemoveFilter(filter))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t(`report.billing.filters.activeTag.${filter.field}`)) + ": " + _toDisplayString(filter.label), 1)
              ]),
              _: 2
            }, 1032, ["onRemove"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (headerStats.value)
      ? (_openBlock(), _createElementBlock("dl", {
          key: 1,
          class: _normalizeClass(["mt-4 grid grid-cols-2 gap-4", ['sm:grid-cols-' + headerStats.value.length]])
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerStats.value, (stat) => {
            return (_openBlock(), _createBlock(_component_UiPanel, {
              key: stat.name
            }, {
              default: _withCtx(() => [
                _createElementVNode("dt", _hoisted_4, _toDisplayString(_ctx.$t('report.billing.stats.' + stat.name)), 1),
                _createElementVNode("dd", _hoisted_5, _toDisplayString(stat.value), 1)
              ]),
              _: 2
            }, 1024))
          }), 128))
        ], 2))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("h2", _hoisted_8, _toDisplayString(_ctx.$t('report.billing.stats.inbound')), 1),
        _createElementVNode("dl", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("dt", _hoisted_11, _toDisplayString(_ctx.$t('report.billing.stats.totalInboundCost')), 1),
            _createElementVNode("dd", _hoisted_12, " R$ " + _toDisplayString(reportInfo.value?.inboundCost?.toFixed(2) || '0,00'), 1)
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(reportInfo.value?.costByOriginType || {}, (value, key) => {
            return (_openBlock(), _createElementBlock("div", {
              key: key,
              class: "flex justify-between"
            }, [
              _createElementVNode("dt", _hoisted_13, _toDisplayString(_ctx.$t(`report.billing.stats.originType.${key}`)), 1),
              _createElementVNode("dd", _hoisted_14, " R$ " + _toDisplayString(value?.toFixed(2) || '0,00'), 1)
            ]))
          }), 128))
        ])
      ]),
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("h2", _hoisted_16, _toDisplayString(_ctx.$t('report.billing.stats.outbound')), 1),
        _createElementVNode("dl", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("dt", _hoisted_19, _toDisplayString(_ctx.$t('report.billing.stats.totalOutboundCost')), 1),
            _createElementVNode("dd", _hoisted_20, " R$ " + _toDisplayString(reportInfo.value?.outboundCost?.toFixed(2) || '0,00'), 1)
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(reportInfo.value?.costByDestinationType || {}, (value, key) => {
            return (_openBlock(), _createElementBlock("div", {
              key: key,
              class: "flex justify-between"
            }, [
              _createElementVNode("dt", _hoisted_21, _toDisplayString(_ctx.$t(`report.billing.stats.destinationType.${key}`)), 1),
              _createElementVNode("dd", _hoisted_22, " R$ " + _toDisplayString(value?.toFixed(2) || '0,00'), 1)
            ]))
          }), 128))
        ])
      ])
    ]),
    _createVNode(_component_BarChart, {
      title: dailyConsumptionChart.value.title,
      categoryData: dailyConsumptionChart.value.category,
      seriesData: dailyConsumptionChart.value.series,
      column: true,
      class: "h-[400px]"
    }, null, 8, ["title", "categoryData", "seriesData"])
  ]))
}
}

})